import { defaults, findIndex, isEqual, map } from 'es-toolkit/compat';
import { useBlurFinalFormOnChange } from 'hooks/useBlurFinalFormOnChange';
import { type ChangeEvent, type FC, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { Field } from 'react-final-form';
import { classes } from 'typestyle';
import { createUniqueElementId } from '../../app.util';
import { FormLabelWithOptional } from './FormLabelWithOptional';
import type { CommonFormInputProps, FormGroupProps } from './form.types';

export interface JFormSelectProps extends CommonFormInputProps, FormGroupProps {
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  options: any[];
  valueField?: string;
  textField?: string;
  // The text of the option that represents no selection. If not given, that option will not exist.
  // The value of the unselected item will be null
  first?: string;
  // Can the user select the first field
  firstDisabled?: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  onChange?: (value: any) => void;
  labelStyle?: string;
  blurOnChange?: boolean;
  helperText?: string;
  helperTextClassName?: string;
}

/**
 * A <select> that connects to the FinalForm and handles arbitrary data types in option values (not just limited
 * to strings like a normal <select> is)
 */
export const JFormSelect: FC<JFormSelectProps> = ({
  label,
  validate,
  description,
  optional,
  className,
  labelStyle,
  options,
  first,
  textField = 'text',
  valueField = 'value',
  onChange,
  firstDisabled,
  name,
  blurOnChange = false,
  helperText,
  helperTextClassName,
  ...inputProps
}) => {
  const inputId = useMemo(() => createUniqueElementId('checkbox'), []);
  const defaultInputProps = defaults(inputProps, {
    autoComplete: 'off',
  });

  useBlurFinalFormOnChange({
    disabled: !blurOnChange,
    name,
  });

  return (
    <Form.Group className={className} controlId={inputId}>
      <FormLabelWithOptional
        className={labelStyle}
        htmlFor={inputId}
        label={label && (typeof label === 'string' ? label : label())}
        optional={optional}
      />
      {helperText && <Form.Text className={classes(helperTextClassName, 'text-muted', 'mb-1')}>{helperText}</Form.Text>}
      <Field format={(value) => findIndex(options, (o) => isEqual(o[valueField], value))} name={name} validate={validate}>
        {(fieldProps) => (
          <>
            <Form.Control
              as="select"
              data-cy={`select-${name}`}
              isInvalid={fieldProps.meta.invalid}
              {...defaultInputProps}
              {...fieldProps.input}
              custom
              onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                const optIndex = parseInt(event.target.value, 10);
                const selectedOpt = options[optIndex];
                const value = selectedOpt?.[valueField] ?? null;
                fieldProps.input.onChange(value);
                onChange?.(value);
              }}
            >
              {typeof first === 'string' && (
                <option disabled={firstDisabled} value={-1}>
                  {first}
                </option>
              )}
              {map(options, (opt, index) => (
                <option key={index} value={index}>
                  {opt[textField]}
                </option>
              ))}
            </Form.Control>
            {fieldProps.meta.invalid && <Form.Control.Feedback type="invalid">{fieldProps.meta.error}</Form.Control.Feedback>}
          </>
        )}
      </Field>
      {description && (
        <Form.Text id={`${inputId}-description`} muted>
          {typeof description === 'string' ? description : description()}
        </Form.Text>
      )}
    </Form.Group>
  );
};
